import type { WidenStrings } from '../widenStrings'
import { esCommon } from './namespaces/common.es'
import { esBridge, esPoolUi, esProfile, esSwapUi, esTokenDetail, esTradeSettings } from './namespaces/trading.es'
import {
  esExplore,
  esHomeDashboard,
  esPortfolio,
  esShell,
  esWallet,
} from './namespaces/ui.es'

export const esMessages = {
  brand: {
    tagline: 'DEX INTERGALÁCTICO',
    infoMenu: 'Información',
    goToSwap: 'Ir a Swap',
    commandPaletteHint: 'Pulsa ⌘K para abrir la paleta de comandos',
  },
  nav: {
    home: 'Home',
    swap: 'Swap',
    explore: 'Explorar',
    portfolio: 'Portfolio',
    bridge: 'Puente',
    settings: 'Ajustes',
    about: 'Quiénes somos',
    legal: 'Jurisdicción',
    contact: 'Medios de contacto',
    designSystem: 'Design System',
    navigation: 'Navegación',
    application: 'Aplicación',
    information: 'Información',
    internal: 'Internal',
  },
  pageTitles: {
    home: 'Inicio',
    explore: 'Explorar',
    portfolio: 'Portfolio',
    movements: 'Movimientos',
    pool: 'Pool',
    positions: 'Mis posiciones',
    token: 'Token',
    about: 'Quiénes somos',
    legal: 'Jurisdicción',
    contact: 'Medios de contacto',
    bridge: 'Bridge',
    designSystem: 'Design System',
    settings: 'Ajustes',
    markets: 'Mercados',
    fallback: "Don't Panic",
  },
  pageHeaders: {
    explore: {
      subtitle:
        'Explora tokens, mercados y pools del ecosistema. Cada listado abre su ficha de detalle antes de operar.',
    },
    markets: {
      subtitle:
        'Explora pares spot del ecosistema y accede a la ficha de cada token para profundizar.',
    },
    tokenDetail: {
      subtitle:
        'Consulta contrato, mercado, liquidez asociada, gráfica y enlaces oficiales antes de operar.',
    },
    swap: {
      subtitle: 'Ejecuta intercambios spot con cotización en pools V2 del ecosistema.',
    },
    liquidityEcosystem: {
      subtitle:
        'Descubre pools activas, compara métricas agregadas y abre el detalle de cada par.',
    },
    poolDetail: {
      subtitle:
        'Revisa reservas, métricas del par y contratos on-chain antes de aportar o retirar liquidez.',
    },
    liquidityPositions: {
      subtitle:
        'Supervisa tu exposición LP, elige una posición y accede a los flujos de gestión.',
    },
    addLiquidity: {
      subtitle: 'Aporta tokens a un par existente y recibe LP tokens según tu participación.',
    },
    importPool: {
      subtitle: 'Recupera pares LP ausentes de tu lista para gestionarlos desde la plataforma.',
    },
    removeLiquidity: {
      subtitle:
        'Retira parte o la totalidad de tu participación y recibe los tokens subyacentes más fees acumulados.',
    },
    portfolio: {
      subtitle: 'Vista consolidada de balances y activos en tu wallet conectada.',
    },
    portfolioConnect: {
      subtitle: 'Vincula tu wallet para desbloquear balances, activos y el resumen de cartera.',
    },
    portfolioMovements: {
      subtitle: 'Registro cronológico de operaciones ejecutadas desde esta plataforma.',
    },
    about: {
      subtitle:
        "Qué es Don't Panic, el Nutri-Matic, nuestros principios y el aviso antes de operar.",
    },
    legal: {
      subtitle:
        'Manifiestos que promulgan nuestra jurisdicción: el ciberespacio. Léelos, descárgalos o elige idioma.',
    },
    contact: {
      subtitle: 'Sigue nuestras redes y únete a la conversación en X y Telegram.',
    },
    bridge: {
      subtitle: 'Espacio reservado para transferencias cross-chain cuando la lógica esté disponible.',
    },
    profile: {
      subtitle: 'Identidad y cartera conectada en la plataforma.',
    },
    designSystem: {
      subtitle:
        'Referencia interna de componentes, tokens visuales y patrones de interfaz del producto.',
    },
    settings: {
      subtitle:
        'Ajusta preferencias de trading, idioma y gestiona la conexión de tu wallet desde un solo lugar.',
    },
  },
  home: {
    subtitle: 'THE INTERGALACTIC DECENTRALIZED EXCHANGE',
    lead:
      "A mostly harmless financial protocol for trading across the known universe. Powered by the Heart of Gold's Infinite Improbability Drive technology.",
    tryNutrimatic: 'Probar Nutri-Matic',
    whatIsDontPanic: "Qué es Don't Panic",
    exploreEcosystem: 'Explorar el ecosistema',
    joinConversation: 'Únete a la conversación',
    joinLead: "Sigue nuestras actualizaciones y forma parte de la comunidad Don't Panic.",
    followX: 'Seguir en X',
    joinTelegram: 'Unirse al canal',
  },
  about: {
    headerSub: 'Plataforma de exchange descentralizado en Infinite Drive',
    meaningTitle: "¿Qué significa Don't Panic?",
    meaningLead:
      "Don't Panic significa no te asustes — en grandes y amigables letras, como en la portada de la Guía del autoestopista galáctico.",
    whatIsTitle: "Qué es Don't Panic",
    whatIsP1:
      "Don't Panic es una plataforma de exchange descentralizado para operar en Infinite Drive. Un protocolo financiero mayormente inofensivo, impulsado por el Infinite Improbability Drive del Heart of Gold, que hace accesible el trading, la liquidez y la exploración de mercado on-chain.",
    whatIsP2:
      'No somos un banco, ni un bróker, ni una custodia. Somos una plataforma para que operes con tus propias claves — con la calma que merece quien sabe dónde está su toalla.',
    nutrimaticTitle: 'El Nutri-Matic',
    nutrimaticP1:
      "El Nutri-Matic es el sintetizador digital de activos de Don't Panic: la cabina desde la que haces swap, revisas cotizaciones y confirmas transacciones. Está inspirado en el legendario dispensador del Heart of Gold — famoso por producir líquidos «casi, pero no del todo, enteramente distintos al té». Esta versión entrega lo que pides.",
    nutrimaticP2:
      'El Nutri-Matic no impulsa el protocolo; es el dispositivo con el que interactúas. El motor es el Infinite Improbability Drive; la red, Infinite Drive.',
    principlesTitle: 'Principios',
    principles: [
      {
        title: 'Autocustodia',
        copy: 'Tus claves, tus activos. No custodiamos fondos ni tenemos acceso a tu wallet.',
      },
      {
        title: 'On-chain y verificable',
        copy: 'Swaps y liquidez se ejecutan en contratos publicados en Infinite Drive.',
      },
      {
        title: 'Hecho para operar',
        copy: 'Trading, pools y portfolio accesibles desde desktop y móvil.',
      },
    ],
    noticeTitle: 'Aviso importante',
    noticeP1:
      "Don't Panic es mayormente inofensivo, pero el DeFi tampoco premia al viajero distraído. Haz tu propia investigación antes de operar. Esta plataforma no ofrece asesoría financiera ni custodia de activos.",
    noticeP2Prefix: 'Nuestra jurisdicción es el ciberespacio — no un territorio con jueces y carpetas, sino el marco digital en el que promulgamos cómo entendemos soberanía, privacidad y comercio sin intermediarios.',
    noticeLegalLink: 'Consulta los manifiestos en Jurisdicción',
    relatedLinksAria: 'Enlaces relacionados',
    linkLegal: 'Jurisdicción',
    linkContact: 'Medios de contacto',
    linkHome: 'Volver al inicio',
  },
  contact: {
    lead:
      "Sigue nuestras actualizaciones, únete a la conversación y entérate primero de lo que viene en Don't Panic.",
    xCaption: 'Anuncios y novedades del ecosistema',
    telegramCaption: 'Comunidad, avisos y conversación',
    followX: 'Seguir en X',
    joinTelegram: 'Unirse al canal',
    firstTimePrefix: '¿Primera vez aquí?',
    firstTimeLink: 'Conoce la plataforma',
    footerNote: 'Los enlaces externos abren en una pestaña nueva. Para textos fundacionales, consulta',
    footerLegalLink: 'Jurisdicción',
  },
  settings: {
    title: 'Configuración',
    sectionsNavAria: 'Secciones de configuración',
    sections: {
      account: {
        label: 'Cuenta',
        description: 'Estado de conexión de tu wallet y opciones de sesión.',
      },
      language: {
        label: 'Idioma',
        description: 'Elige el idioma de la plataforma o déjalo en automático según tu navegador.',
      },
      trade: {
        label: 'Trade',
        description: 'Slippage y plazo límite para intercambios spot.',
      },
    },
    language: {
      preferenceLabel: 'Preferencia de idioma',
      auto: 'Automático',
      autoHint: 'Detectado automáticamente según las preferencias de idioma de tu navegador para sitios web.',
      es: 'Español',
      en: 'English',
    },
  },
  legal: {
    localeLabel: 'Idioma',
    localeSelectorAria: 'Seleccionar idioma del documento',
    intro:
      'Textos fundacionales del ciberespacio — Declaración de Independencia del Ciberespacio, Manifiesto Criptoanarquista y Manifiesto Cypherpunk. Cada documento incluye enlace a su publicación original.',
    footerNote:
      "Estos manifiestos no son asesoría financiera. Para el contexto del producto — Don't Panic, el Nutri-Matic y cómo operamos — consulta",
    footerAbout: 'Quiénes somos',
    footerOr: 'o',
    footerContact: 'Medios de contacto',
  },
  common: esCommon,
  wallet: esWallet,
  portfolio: esPortfolio,
  explore: esExplore,
  homeDashboard: esHomeDashboard,
  shell: esShell,
  swapUi: esSwapUi,
  poolUi: esPoolUi,
  tokenDetail: esTokenDetail,
  bridge: esBridge,
  profile: esProfile,
  tradeSettings: esTradeSettings,
  settingsAccount: {
    description:
      'Consulta si tu wallet está conectada a la red correcta. Desde aquí puedes abrir el panel de cuenta, cambiar de red o desconectar la sesión activa.',
  },
} as const

export type Messages = WidenStrings<typeof esMessages>
